import {AfterViewInit, Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Location} from '@angular/common';
import {AuthService} from "@app/core/services/auth.service";
import {HTTP_STATUS_CODE} from "@app/core/constants/system.constants";
import {environment} from "@env/environment";

declare var window: any;
declare var grecaptcha: any;

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit, AfterViewInit {
  bgLoginPath = environment.bgLogin;
  form: FormGroup = this.fb.group({
    loginName: [null, Validators.required],
    oldPassword: [null, Validators.required],
    newPassword: [null, Validators.required],
    retypePassword: [null, Validators.required],
    captcha: [null, Validators.required],
    // 2FA — chỉ bắt buộc nếu tài khoản đã bật 2FA (BE tự kiểm tra)
    totpCode: [null]
  });
  isSubmitted = false;
  isFocus1 = false;
  isFocus2 = false;
  isFocus3 = false;
  captcha = '';
  isLoading = false;
  captchaToken: string | null = null;
  show2fa = false; // chỉ hiện ô nhập mã 2FA khi BE báo tài khoản đã bật 2FA

  constructor(
    private authService: AuthService,
    private readonly router: Router,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private location: Location
  ) {
  }

  passwordRules = {
    length: false,
    complexity: false,
    special: false
  };

  ngOnInit(): void {
    window.onCaptchaResolved = (token: string) => {
      this.captchaToken = token;
      this.form.get('captcha')?.setValue(token);
    };
    this.form.get('newPassword')?.valueChanges.subscribe(value => {
      this.checkPasswordRules(value || '');
    });
    // Chỉ hỏi trạng thái 2FA khi đã đăng nhập.
    // Từ trang login (chưa có token) gọi /mfa/status sẽ 401 → interceptor đẩy về login.
    if (this.authService.isAuthenticated()) {
      this.authService.mfaStatus().subscribe({
        next: (res) => {
          if (res?.data?.enrolled) {
            this.show2fa = true;
          }
        },
        error: () => {
          // Bỏ qua: BE vẫn tự kiểm tra khi submit (trả MFA_REQUIRED nếu cần)
        }
      });
    }
  }

  checkPasswordRules(password: string) {
    this.passwordRules.length = password.length >= 8;
    this.passwordRules.complexity =
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password);
    this.passwordRules.special =
      /[~!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (window.grecaptcha) {
        clearInterval(interval);
        this.renderCaptcha();
      }
    }, 300);
  }

  renderCaptcha(): void {
    if (!window.grecaptcha) return;
    const el = document.getElementById('recaptcha');
    if (!el) return;
    el.innerHTML = '';
    grecaptcha.render('recaptcha', {
      sitekey: environment.siteKeyCaptcha,
      callback: (token: string) => {
        this.captchaToken = token;
        this.form.get('captcha')?.setValue(token);
      }
    });
  }

  onInputChange1($event: any) {
    if ($event === '') {
      this.isFocus1 = false;
    }
  }

  onInputChange2($event: any) {
    if ($event === '') {
      this.isFocus2 = false;
    }
  }

  onInputChange3($event: any) {
    if ($event === '') {
      this.isFocus3 = false;
    }
  }

  get f() {
    return this.form.controls;
  }

  onLabelClick1() {
    this.isFocus1 = !this.isFocus1;
  }

  onLabelClick2() {
    this.isFocus2 = !this.isFocus2;
  }

  onLabelClick3() {
    this.isFocus3 = !this.isFocus3;
  }

  preventSpace(event: KeyboardEvent) {
    if (event.key === ' ') {
      event.preventDefault();
    }
  }

  back() {
    this.location.back();
  }

  submitForm(): void {
    this.isSubmitted = true;
    if (!this.captchaToken) {
      this.toastrService.error(
        this.translate.instant('common.user.validate.errorCaptcha')
      );
      return;
    }

    if (this.form.valid) {
      if (this.f.newPassword.value === this.f.oldPassword.value) {
        this.toastrService.error(this.translate.instant('common.user.validate.errorPassword'));
        return;
      }

      if (this.f.newPassword.value !== this.f.retypePassword.value) {
        this.toastrService.error(this.translate.instant('common.user.validate.errorRetypePassword'));
        return;
      }

      if (this.validatePassword(this.f.newPassword.value) !== null) {
        this.toastrService.error(this.validatePassword(this.f.newPassword.value));
        return;
      }
      this.isLoading = true;
      const fromObject = {
        ...this.form.value,
        captchaToken: this.captchaToken
      };
      this.authService.changePassword(fromObject).subscribe({
        next: (res) => {
          if (res.code === HTTP_STATUS_CODE.SUCCESS) {
            this.toastrService.success(
              this.translate.instant('common.user.validate.success')
            );
            this.location.back();
          } else if (res.code === 'MFA_REQUIRED') {
            this.isLoading = false;
            this.show2fa = true;
            this.toastrService.info('Tài khoản có bật xác thực 2 lớp, vui lòng nhập mã 2FA.');
            this.resetCaptcha();
          } else {
            this.isLoading = false;
            this.toastrService.error(res?.message);
            this.resetCaptcha();
          }
        },
        error: () => {
          this.isLoading = false;
          this.resetCaptcha();
        }
      });

    }
  }

  /** Captcha reCAPTCHA chỉ dùng được 1 lần — phải render lại token mới trước khi cho submit lại. */
  private resetCaptcha(): void {
    this.captchaToken = null;
    this.form.get('captcha')?.reset();
    setTimeout(() => this.renderCaptcha());
  }

  validatePassword(password) {
    const specialCharacters = /[~!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
    const lowercase = /[a-z]/;
    const uppercase = /[A-Z]/;
    const digits = /[0-9]/;
    if (password.length < 8) {
      return this.translate.instant('common.user.validate.error');
    }
    if (!lowercase.test(password) || !uppercase.test(password) || !digits.test(password) || !specialCharacters.test(password)) {
      return this.translate.instant('common.user.validate.error');
    }
    return null;
  }
}
